import type { Question } from "@components/Sender/QuestionItem";
import { ROUTER_PATH } from "@config/router";
import { getBridge } from "@/src/lib/bridge/bridge";
import { isInNativeChatShellModal } from "@/src/lib/bridge/shellAppNavigation";
import { trackOnboardingAnalytics } from "@/src/hooks/onboarding/onboardingAnalytics";
import { isOnboardingRoute } from "@/src/hooks/onboarding/isOnboardingRoute";

type ShellRouter = {
  replace: (href: string) => void;
};

type OpenChatModalFn = (params: { questions?: Question[] }) => void;

export type NavigateExitOnboardingToMainChatParams = {
  isPC: boolean;
  router: ShellRouter;
  openChatModal: OpenChatModalFn;
  /** 移动端弹窗推荐问题；默认取前 4 条 */
  questions?: Question[];
  /** 当前 pathname；用于判断是否已在目标壳页面 */
  pathname?: string;
  /** 聊天弹窗是否已打开（移动端）；由调用方读取 ChatStore.hasChatModal */
  isChatModalOpen?: boolean;
};

/** PC Chat Tab 路由（含子路径） */
export const isChatShellRoute = (pathname: string): boolean => {
  return (
    pathname === ROUTER_PATH.CHAT || pathname.startsWith(`${ROUTER_PATH.CHAT}/`)
  );
};

/** 移动端主 Tab 壳（/invest） */
export const isInvestShellRoute = (pathname: string): boolean => {
  return (
    pathname === ROUTER_PATH.INVEST ||
    pathname.startsWith(`${ROUTER_PATH.INVEST}/`)
  );
};

/** Brief 主 Tab 壳（/brief） */
export const isBriefShellRoute = (pathname: string): boolean => {
  return (
    pathname === ROUTER_PATH.BRIEF ||
    pathname.startsWith(`${ROUTER_PATH.BRIEF}/`)
  );
};

export const isOnboardingExitRoute = (pathname: string): boolean => {
  return isOnboardingRoute(pathname);
};

/**
 * onboarding 流程结束后退出主壳。
 * - PC：`/chat` Tab，由 ChatTabContent 初始化会话
 * - App Chat WebView：SPA 到 `/app-chat-modal`，embedded 静默升为 modal（无上滑），底层切 Plan Tab；关闭时向下滑出
 * - H5：从 onboarding 页 → `/brief`，并打开 ChatModal 推荐问题
 * - 已在目标页或弹窗已打开时，跳过重复跳转/打开
 */
export const navigateExitOnboardingToMainChat = ({
  isPC,
  router,
  openChatModal,
  questions = [],
  pathname = "",
  isChatModalOpen,
}: NavigateExitOnboardingToMainChatParams): void => {
  trackOnboardingAnalytics("ob_funnel_completed");

  if (isPC) {
    if (!isChatShellRoute(pathname)) {
      router.replace(ROUTER_PATH.CHAT);
    }
    return;
  }

  if (getBridge().isInApp() && isInNativeChatShellModal()) {
    router.replace(ROUTER_PATH.APP_CHAT_MODAL);
    getBridge().presentShellChat({
      presentation: "modal",
      questions: questions.slice(0, 4),
    });
    return;
  }

  const chatModalAlreadyOpen = isChatModalOpen ?? false;

  if (isOnboardingExitRoute(pathname) && !isBriefShellRoute(pathname)) {
    router.replace(ROUTER_PATH.BRIEF);
  }

  if (!chatModalAlreadyOpen) {
    openChatModal({
      questions: questions.slice(0, 4),
    });
  }
};
